import { useState, useRef, useEffect, useCallback, KeyboardEvent, ChangeEvent } from 'react';
import styles from './AiQuestionInput.module.scss';
import classnames from 'classnames';
import { useConversation } from '@chewy/chat-library-common';
import { KibAttachIcon, KibArrowUpIcon } from '@chewy/kib-icons-react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import AiBadge from './AiBadge/AiBadge';

export interface AiQuestionInputProps {
    submitQuestion: (question: string, conversationEntryPoint?: string, attachments?: File[]) => void;
    type: 'aibot' | 'agent';
    label?: string;
    subject: string;
    placeholder?: string;
    placeholderSuffix?: string;
    disabled?: boolean;
    /** Controlled draft text. When provided, the parent owns the textarea value. */
    value?: string;
    onTextChange: (text: string) => void;
    onAddAttachment?: (files: File[]) => void;
    onExpanded?: (isExpanded: boolean) => void;
    conversationEntryPoint?: string;
}

export default function AiQuestionInput({
    submitQuestion,
    type,
    label = 'Chat with',
    subject,
    placeholder = '',
    placeholderSuffix,
    disabled = false,
    onAddAttachment,
    onTextChange,
    value = '',
    onExpanded,
    conversationEntryPoint,
}: AiQuestionInputProps) {
    const [isFocused, setIsFocused] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const isActive = isFocused || value.length > 0;
    const canSubmit = value.trim().length > 0;
    const showCustomPlaceholder = value.length === 0 && !placeholder;

    const adjustTextareaHeight = useCallback(() => {
        if (textareaRef.current && containerRef.current) {
            // Reset height to auto to get the correct scrollHeight
            textareaRef.current.style.height = 'auto';

            // Use visual viewport height if available (accounts for mobile keyboard)
            const viewportHeight = window.visualViewport?.height || window.innerHeight;

            // Non-textarea chrome within the container (padding, buttons, etc.)
            const containerRect = containerRef.current.getBoundingClientRect();
            const textareaRect = textareaRef.current.getBoundingClientRect();
            const textareaLineHeight = parseFloat(getComputedStyle(textareaRef.current).lineHeight);
            const nonTextareaHeight = containerRect.height - textareaRect.height;

            /**
             * Smaller of:
             * - 1/3 of the available height
             * - 5 lines of height
             */
            const maxHeight = Math.min((viewportHeight - nonTextareaHeight) * 0.3, textareaLineHeight * 5);

            const scrollHeight = textareaRef.current.scrollHeight;
            const minHeight = textareaLineHeight; // Single line height
            const expanded = scrollHeight > 80; // ~4 lines
            onExpanded?.(expanded);

            /**
             * Grow the textarea up to maxHeight, then enable scrolling.
             *
             * *`em`s and `rem`s do not work in Android with user-specified font sizes!*
             */
            if (scrollHeight <= maxHeight) {
                textareaRef.current.style.height = `${Math.max(scrollHeight, minHeight)}px`;
                textareaRef.current.style.overflowY = 'hidden';
            } else {
                textareaRef.current.style.height = `${maxHeight}px`;
                textareaRef.current.style.overflowY = 'auto';
            }
        }
    }, [onExpanded]);

    useEffect(() => {
        adjustTextareaHeight();
    }, [value, adjustTextareaHeight]);

    useEffect(() => {
        const visualViewport = window.visualViewport;

        // Recalculate height on window resize or scroll
        window.addEventListener('resize', adjustTextareaHeight);
        window.addEventListener('scroll', adjustTextareaHeight, true);

        // Listen to visual viewport changes (mobile keyboard)
        if (visualViewport) {
            visualViewport.addEventListener('resize', adjustTextareaHeight);
            visualViewport.addEventListener('scroll', adjustTextareaHeight);
        }

        // Initial adjustment on mount
        adjustTextareaHeight();

        return () => {
            window.removeEventListener('resize', adjustTextareaHeight);
            window.removeEventListener('scroll', adjustTextareaHeight, true);

            if (visualViewport) {
                visualViewport.removeEventListener('resize', adjustTextareaHeight);
                visualViewport.removeEventListener('scroll', adjustTextareaHeight);
            }
        };
    }, [adjustTextareaHeight]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleSubmit = () => {
        if (canSubmit && !disabled) {
            submitQuestion(value.trim(), conversationEntryPoint, attachments.length > 0 ? attachments : undefined);
            onTextChange('');
            setAttachments([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleAttachmentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setAttachments(fileArray);
            if (onAddAttachment) {
                onAddAttachment(fileArray);
            }
        }
    };

    return (
        <div
            ref={containerRef}
            className={classnames(styles.aiQuestionInputContainer, {
                [styles.active]: isActive,
            })}
        >
            <div className={styles.inputContainer}>
                {type === 'agent' && (
                    <>
                        <KibButtonNew
                            type="button"
                            icon={<KibAttachIcon />}
                            iconOnly
                            emphasis="subtle"
                            theme="utility"
                            size="medium"
                            onClick={handleAttachmentClick}
                            disabled={disabled}
                            aria-label="Attach image"
                            className={styles.attachButton}
                        >
                            Attach
                        </KibButtonNew>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className={styles.fileInput}
                            onChange={handleFileChange}
                        />
                    </>
                )}

                {showCustomPlaceholder && (
                    <div className={styles.customPlaceholder} aria-hidden="true">
                            {label}
                            {' '}

                            {type === 'aibot' ?
                             <AiBadge subject={subject} placeholderSuffix={placeholderSuffix} />
                            : <span className={styles.placeholderSubject}>{subject}</span>}

                    </div>
                )}
                <textarea
                    id="ai-question-input-textarea"
                    role="textbox"
                    ref={textareaRef}
                    className={classnames(styles.textarea, {
                        [styles.textarea_withButton]: type === 'agent',
                        [styles.textarea_withCustomPlaceholder]: showCustomPlaceholder,
                    })}
                    value={value}
                    onChange={event => onTextChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setIsFocused(true);
                        // Scroll input into view when keyboard appears
                        setTimeout(() => {
                            adjustTextareaHeight();
                            textareaRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest'
                            });
                        }, 300); // Delay to wait for keyboard animation
                    }}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    aria-label={`Chat with ${subject}, type your message`}
                    rows={1}
                />

                {isActive && canSubmit && (
                    <KibButtonNew
                        size="medium"
                        className={styles.submitButton}
                        disabled={disabled}
                        iconOnly
                        icon={<KibArrowUpIcon />}
                        onClick={handleSubmit}
                    >
                        Send message
                    </KibButtonNew>
                )}
            </div>
        </div>
    );
}
